/**
 * Diagnostic primitives for the Event App.
 *
 * These helpers are logging-only: they never change user-visible behavior,
 * retry policy, redirects, toasts, recovery, Datadog, or Segment. Every value
 * that may reach a log line is bounded to MAX_DIAGNOSTIC_STRING_LENGTH and the
 * fallback path omits tokens, request bodies, authorization headers, raw query
 * URLs, stacks, and raw provider objects — it logs only the event name.
 *
 * Correlation and operation IDs are locally generated opaque values
 * (`crypto.randomUUID()` when available, a timestamp/random fallback otherwise).
 */
import loggerService from './logger.service';

const MAX_DIAGNOSTIC_STRING_LENGTH = 200;

let pageCorrelationId: string | undefined;

function generateOpaqueId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // crypto may be unavailable in some environments — fall through.
  }
  // Local fallback: timestamp + random — opaque, no embedded user data.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function toDiagnosticString(value: unknown): string {
  try {
    return String(value);
  } catch {
    return '';
  }
}

/**
 * Returns a single opaque correlation ID for the page lifetime. The value is
 * generated once and cached, so repeated calls return the same string.
 */
export function getCorrelationId(): string {
  if (!pageCorrelationId) {
    pageCorrelationId = capDiagnosticString(generateOpaqueId()) ?? generateOpaqueId();
  }
  return pageCorrelationId;
}

/**
 * Produces a one-off opaque operation ID prefixed with the given label.
 * Each call yields a distinct suffix. The total length is bounded to
 * MAX_DIAGNOSTIC_STRING_LENGTH by truncating the prefix as needed.
 */
export function createOperationId(prefix: string): string {
  const suffix = generateOpaqueId();
  const separator = ':';
  const maxPrefix = MAX_DIAGNOSTIC_STRING_LENGTH - separator.length - suffix.length;
  let boundedPrefix = prefix;
  if (maxPrefix <= 0) {
    boundedPrefix = '';
  } else if (boundedPrefix.length > maxPrefix) {
    boundedPrefix = boundedPrefix.slice(0, maxPrefix);
  }
  const result = `${boundedPrefix}${separator}${suffix}`;
  // Final safety: never exceed the bound even if the suffix is unusually long.
  return result.length > MAX_DIAGNOSTIC_STRING_LENGTH ? result.slice(0, MAX_DIAGNOSTIC_STRING_LENGTH) : result;
}

/**
 * Bounds a value to MAX_DIAGNOSTIC_STRING_LENGTH characters, appending an
 * ellipsis when truncation occurs. Returns `undefined` for null/undefined or
 * empty input so callers can conditionally spread the result.
 */
export function capDiagnosticString(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const str = toDiagnosticString(value);
  if (str.length === 0) {
    return undefined;
  }
  // Total length (content + ellipsis) must stay within the bound.
  return str.length > MAX_DIAGNOSTIC_STRING_LENGTH ? `${str.slice(0, MAX_DIAGNOSTIC_STRING_LENGTH - 1)}…` : str;
}

/**
 * Normalizes an unknown error into a short failure-category label.
 * Recognizes `AbortError` and abort/cancel markers as `cancelled`, and
 * timeout-shaped errors (name or message containing "timeout"/"timed out")
 * as `timeout`. Anything else resolves to the provided `fallback`.
 */
export function classifyDiagnosticError(error: unknown, fallback: string): string {
  let name = '';
  let message = '';

  if (error instanceof Error) {
    name = toDiagnosticString(error.name);
    message = toDiagnosticString(error.message);
  } else if (error && typeof error === 'object') {
    try {
      const e = error as Record<string, unknown>;
      name = toDiagnosticString(e.name ?? '');
      message = toDiagnosticString(e.message ?? '');
    } catch {
      name = '';
      message = '';
    }
  } else if (typeof error === 'string') {
    message = error;
  }

  const lcName = name.toLowerCase();
  const lcMessage = message.toLowerCase();

  if (lcName === 'aborterror' || lcName === 'abort' || lcMessage.includes('abort') || lcMessage.includes('cancel')) {
    return 'cancelled';
  }

  if (lcName.includes('timeout') || lcMessage.includes('timeout') || lcMessage.includes('timed out')) {
    return 'timeout';
  }

  return capDiagnosticString(fallback) ?? fallback;
}

/**
 * Best-effort diagnostic: calls `loggerService.info(event, metadata)` with the
 * event name bounded to MAX_DIAGNOSTIC_STRING_LENGTH. If that throws, attempts
 * at most one `loggerService.warn('diagnostic_logging_failed', { event })` —
 * using the bounded event name, never the original thrown value or metadata —
 * then swallows any fallback failure. The fixed health event name
 * `diagnostic_logging_failed` is a short literal and is never capped. Never
 * throws.
 */
export function safeDiagnosticInfo(event: string, metadata: Record<string, unknown>): void {
  const boundedEvent = capDiagnosticString(event) ?? event;
  try {
    loggerService.info(boundedEvent, metadata);
  } catch {
    try {
      loggerService.warn('diagnostic_logging_failed', { event: boundedEvent });
    } catch {
      // Swallow fallback errors — diagnostics must never change control flow.
    }
  }
}
